import { action, makeAutoObservable, observable, runInAction } from "mobx";
import { debounce } from "lodash";
import { toast } from "../components/ui/toast";

import type { PaginationInfo } from "../types/WorkflowsResponse";
import {
  deleteWorkflow,
  importWorkflow,
  publishWorkflow,
  exportWorkflow,
  duplicateWorkflow,
  updateWorkflowStatus,
  fetchWorkflows,
  getWorkflow,
  getActiveWorkflowNames,
  updateWorkflowNodes,
  getWorkflowStatistics,
  getWorkflowPerformance,
  getWorkflowAnalytics,
  getWorkflowVersions,
  validateWorkflow,
  getWorkflowTemplates,
  createWorkflowFromTemplate,
  createTestSuite,
  runTestScenario,
  getTestHistory,
  getWorkflowExecutions,
  getWorkflowExecution,
  getWorkflowExecutionLogs,
  deleteWorkflowExecution,
  getWorkflowVariables,
  updateWorkflowVariables,
  addWorkflowVariable,
  deleteWorkflowVariable,
  getWorkflowKeywords,
  updateWorkflowKeywords,
  addWorkflowKeyword,
  deleteWorkflowKeyword,
  simulateWorkflow,
  getSimulationHistory,
  archiveWorkflow as archiveWorkflowService,
  restoreWorkflow as restoreWorkflowService,
  pauseWorkflow as pauseWorkflowService,
  activateWorkflow as activateWorkflowService,
  updateWorkflow as apiUpdateWorkflow,
} from "../services/workflows.service";
import {
  Workflow,
  IWorkflowNode,
  IWorkflowWithChannelFlag,
  WorkflowNodesStatusEnum,
  WorkflowStatusEnum,
} from "src/types";
import axiosInstance from "../core/axios";

export type AutoSaveStatus = 'idle' | 'saving' | 'saved' | 'error' | 'conflict';

export default class WorkflowStore {
  data: Workflow[] = [];
  currentWorkflow: Workflow | undefined;
  private debouncedUpdateWorkflowNodes: () => void;
  private isSaveInFlight = false;
  private pendingSave = false;
  autoSaveStatus: AutoSaveStatus = 'idle';
  autoSaveError: string | null = null;
  activeNames: Pick<Workflow, "id" | "name">[] = [];

  // New observables for enhanced functionality
  workflowStatistics: any = null;
  workflowPerformance: any = null;
  workflowAnalytics: any = null;
  workflowVersions: any[] = [];
  workflowTemplates: any[] = [];
  testSuites: any[] = [];
  testHistory: any[] = [];
  workflowExecutions: any[] = [];
  currentExecution: any = null;
  executionLogs: any[] = [];
  workflowVariables: Record<string, any[]> = {};
  workflowKeywords: Record<string, any[]> = {};
  simulationHistory: any[] = [];

  // Workflow name cache for campaign display (1-hour TTL)
  workflowNameCache: Map<string, { name: string; fetchedAt: Date }> = new Map();
  private readonly WORKFLOW_CACHE_TTL = 3600000; // 1 hour in milliseconds

  currentPaginationInfo: PaginationInfo & {
    page?: number;
    currentPage?: number;
    pageSize?: number;
    hasPreviousPage?: boolean;
  } = {
    totalPages: 0,
    totalItems: 0,
    hasNextPage: true,
    hasPreviousPage: false,
    pageSize: 10,
    currentPage: 1,
    page: 1,
  };

  page = 1;
  limit = 10;
  search = "";
  status = "all";
  nodesStatus = "all";
  category = "all";
  isLoading = true;
  hasMore = true;

  // Loading states for different operations
  isLoadingStatistics = false;
  isLoadingPerformance = false;
  isLoadingAnalytics = false;
  isLoadingVersions = false;
  isLoadingTemplates = false;
  isLoadingTestSuites = false;
  isLoadingExecutions = false;
  isLoadingVariables = false;
  isLoadingKeywords = false;
  isWorkflowLoading = false;

  constructor() {
    makeAutoObservable(this, {
      data: observable,
      currentPaginationInfo: observable,
      currentWorkflow: observable,
      limit: observable,
      search: observable,
      status: observable,
      nodesStatus: observable,
      category: observable,
      activeNames: observable,
      isLoading: observable,
      hasMore: observable,
      workflowStatistics: observable,
      workflowPerformance: observable,
      workflowAnalytics: observable,
      workflowVersions: observable,
      workflowTemplates: observable,
      testSuites: observable,
      testHistory: observable,
      workflowExecutions: observable,
      currentExecution: observable,
      executionLogs: observable,
      workflowVariables: observable,
      workflowKeywords: observable,
      simulationHistory: observable,
      workflowNameCache: observable,
      isLoadingStatistics: observable,
      isLoadingPerformance: observable,
      isLoadingAnalytics: observable,
      isLoadingVersions: observable,
      isLoadingTemplates: observable,
      isLoadingTestSuites: observable,
      isLoadingExecutions: observable,
      isLoadingVariables: observable,
      isLoadingKeywords: observable,
      isWorkflowLoading: observable,
      autoSaveStatus: observable,
      autoSaveError: observable,
      setPageNum: action,
      setLimit: action,
      setSearchTerm: action,
      setStatus: action,
      setNodesStatus: action,
      setCategory: action,
      resetFilters: action,
      getWorkflowName: action,
      setWorkflowNameCache: action,
      clearWorkflowNameCache: action,
    });

    this.debouncedUpdateWorkflowNodes = debounce(
      this.updateWorkflowNodes,
      2000,
    );
  }

  fetchData = async (reset = false) => {
    try {
      runInAction(() => {
        this.isLoading = true;
      });

      const { data } = await fetchWorkflows({
        page: this.page,
        limit: this.limit,
        search: this.search,
        status: this.status !== "all" ? this.status : undefined,
        nodesStatus: this.nodesStatus !== "all" ? this.nodesStatus : undefined,
      });

      runInAction(() => {
        // DEBUG: Log what we're getting from API
        console.log("API Response pagination:", data.pagination);

        const currentPage = parseInt(data.pagination.page) || this.page;
        const totalPages = data.pagination.totalPages || 0;
        const totalItems = data.pagination.totalItems || 0;

        // Calculate hasPreviousPage correctly
        const hasPreviousPage = currentPage > 1;

        // Calculate hasNextPage correctly - use API's hasNextPage if available, otherwise calculate
        const hasNextPage =
          data.pagination.hasNextPage !== undefined
            ? data.pagination.hasNextPage
            : currentPage < totalPages;

        this.currentPaginationInfo = {
          page: currentPage,
          totalItems: totalItems,
          totalPages: totalPages,
          hasNextPage: hasNextPage,
          hasPreviousPage: hasPreviousPage,
          pageSize: this.limit,
          currentPage: currentPage,
        };

        console.log("Calculated pagination:", this.currentPaginationInfo);

        if (reset || this.page === 1) {
          this.data = [...data.workflows] as unknown as Workflow[];
        } else {
          this.data = [
            ...this.data,
            ...data.workflows,
          ] as unknown as Workflow[];
        }

        this.hasMore = hasNextPage;
        this.isLoading = false;
      });
    } catch (error: any) {
      runInAction(() => {
        this.isLoading = false;
      });
      toast.error(error.message || "Error fetching workflows");
    }
  };

  loadMore = async (page: number) => {
    console.log("loadMore called with page:", page);
    if (!this.isLoading) {
      // Store the requested page
      const requestedPage = page;

      // Update the store's page state
      runInAction(() => {
        this.page = requestedPage;
      });

      // Fetch data for that specific page
      await this.fetchData(true); // true to reset data for the new page
    }
  };

  refreshData = async () => {
    this.page = 1;
    await this.fetchData(true);
  };

  /// add workflow
  addWorkflow = async (data: any) => {
    try {
      const values = await data;
      const response = await axiosInstance.post("/workflow", { ...values });

      runInAction(() => {
        this.data.unshift(response.data.data);
        this.currentPaginationInfo.totalItems += 1;
      });

      toast.success("Workflow created successfully");
    } catch (error: any) {
      toast.error("Invalid information please complete all required fields.");
    }
  };

  deleteWorkflow = async (id: string) => {
    try {
      await deleteWorkflow(id);
      runInAction(() => {
        this.data = this.data.filter(workflow => workflow.id !== id);
        this.currentPaginationInfo.totalItems -= 1;
      });
      toast.success("Workflow deleted successfully");
    } catch (error: any) {
      toast.error("Error deleting workflow");
    }
  };

  importWorkflow = async (file: File) => {
    try {
      const { data } = await importWorkflow(file);
      runInAction(() => {
        this.data.unshift(data as unknown as Workflow);
        this.currentPaginationInfo.totalItems += 1;
      });
      toast.success("Workflow imported successfully");
    } catch (err: any) {
      toast.error(err.response?.data?.message || "Error importing workflow");
    }
  };

  getWorkflow = async (id: string) => {
    try {
      runInAction(() => {
        this.isWorkflowLoading = true;
      });

      const { data } = await getWorkflow(id);
      runInAction(() => {
        this.currentWorkflow = data as unknown as Workflow;
        this.isWorkflowLoading = false;
      });
    } catch (error: any) {
      console.warn(`Workflow with ID ${id} not found:`, error.message);
      if (error.response?.status !== 404) {
        toast.error("Error fetching workflow");
      }
      runInAction(() => {
        this.currentWorkflow = undefined;
        this.isWorkflowLoading = false;
      });
    }
  };

  getActiveWorkflowsNames = async () => {
    try {
      const { data } = await getActiveWorkflowNames();
      runInAction(() => {
        this.activeNames = data as unknown as Pick<Workflow, "id" | "name">[];
      });
    } catch (error: any) {
      console.error("Error fetching active workflow names:", error);
    }
  };

  publishWorkflow = async (id: string) => {
    try {
      await publishWorkflow(id);
      runInAction(() => {
        const now = new Date().toISOString();
        // Update in the data list
        this.data = this.data.map(workflow => {
          if (workflow.id === id) {
            workflow.nodesStatus = WorkflowNodesStatusEnum.Published;
            workflow.updatedAt = now;
          }
          return workflow;
        });
        // Also update currentWorkflow if it's the one being published
        if (this.currentWorkflow?.id === id) {
          this.currentWorkflow.nodesStatus = WorkflowNodesStatusEnum.Published;
          this.currentWorkflow.updatedAt = now;
        }
      });
    } catch (error: any) {
      throw error;
    }
  };

  setWorkflow = (id: string) => {
    this.currentWorkflow = this.data.find(workflow => workflow.id === id);
  };

  updateCurrentWorkflowNodes = (updatedNodes: IWorkflowNode[] | any) => {
    runInAction(() => {
      if (this.currentWorkflow && !this.isWorkflowLoading) {
        // Skip if the nodes haven't actually changed (prevents feedback loops
        // where re-renders push the same data back through onChange → auto-save)
        if (
          this.currentWorkflow.currentNodes &&
          JSON.stringify(this.currentWorkflow.currentNodes) === JSON.stringify(updatedNodes)
        ) {
          return;
        }

        // Always store as flat IWorkflowNode[] tree for backend compatibility
        this.currentWorkflow.currentNodes = updatedNodes;

        // Mark as Draft if currently Published (any change = draft)
        if (
          this.currentWorkflow.nodesStatus ===
          WorkflowNodesStatusEnum.Published
        ) {
          this.currentWorkflow.nodesStatus = WorkflowNodesStatusEnum.Draft;
          // Also update in the data list so the workflows list stays in sync
          const idx = this.data.findIndex(
            w => w.id === this.currentWorkflow!.id,
          );
          if (idx !== -1) {
            this.data[idx].nodesStatus = WorkflowNodesStatusEnum.Draft;
          }
        }

        // Only auto-save if we have actual nodes (supports both array and structure formats)
        const hasData = Array.isArray(updatedNodes)
          ? updatedNodes.length > 0
          : !!(updatedNodes as any)?.flows?.default?.length;
        if (hasData) {
          this.debouncedUpdateWorkflowNodes();
        }
      }
    });
  };

  // Update workflow structure (new format with sub-flows)
  // NOTE: Only stores the flat default-flow tree for backend compatibility.
  // Sub-flow data is managed entirely in the frontend FlowBuilder state.
  updateWorkflowStructure = (structure: any) => {
    runInAction(() => {
      if (this.currentWorkflow && !this.isWorkflowLoading) {
        // Extract flat tree from structure for backend compatibility
        const flatNodes = structure?.flows?.default ?? structure;
        this.currentWorkflow.currentNodes = Array.isArray(flatNodes)
          ? flatNodes
          : [];
        this.debouncedUpdateWorkflowNodes();
      }
    });
  };

  private updateWorkflowNodes = async () => {
    if (!this.currentWorkflow) return;

    // Guard: if a save is already in flight, mark pending and return.
    // The in-flight save will re-trigger when it finishes.
    if (this.isSaveInFlight) {
      this.pendingSave = true;
      return;
    }

    this.isSaveInFlight = true;
    runInAction(() => {
      this.autoSaveStatus = 'saving';
      this.autoSaveError = null;
    });

    // Snapshot what we're saving so we can detect if things changed while saving
    const workflowId = this.currentWorkflow.id;
    const payload = this.currentWorkflow.currentNodes;
    const lastUpdatedAt = this.currentWorkflow.updatedAt;

    try {
      const result = await updateWorkflowNodes(
        workflowId,
        payload,
        WorkflowNodesStatusEnum.Draft,
        lastUpdatedAt,
      );

      runInAction(() => {
        if (this.currentWorkflow?.id === workflowId) {
          // Update the version stamp from server response
          this.currentWorkflow.updatedAt = result.updatedAt || new Date().toISOString();
          this.autoSaveStatus = 'saved';
        }
      });

      // Auto-clear "saved" indicator after 3 seconds
      setTimeout(() => {
        runInAction(() => {
          if (this.autoSaveStatus === 'saved') {
            this.autoSaveStatus = 'idle';
          }
        });
      }, 3000);
    } catch (error: any) {
      const status = error?.response?.status;
      runInAction(() => {
        if (status === 409) {
          // Conflict — another session/tab updated this workflow
          this.autoSaveStatus = 'conflict';
          this.autoSaveError = 'This workflow was modified elsewhere. Reload to get the latest version.';
          toast.error("Conflict: workflow was modified by another session. Please reload.");
        } else {
          this.autoSaveStatus = 'error';
          this.autoSaveError = error?.message || 'Auto-save failed';
          console.error("Autosave failed:", error);
        }
      });
    } finally {
      this.isSaveInFlight = false;

      // If changes arrived while we were saving, trigger another save
      if (this.pendingSave) {
        this.pendingSave = false;
        this.debouncedUpdateWorkflowNodes();
      }
    }
  };

  /** Reload the workflow from server (used after conflict detection) */
  reloadWorkflow = async () => {
    if (!this.currentWorkflow) return;
    const id = this.currentWorkflow.id;
    runInAction(() => {
      this.autoSaveStatus = 'idle';
      this.autoSaveError = null;
    });
    await this.getWorkflow(id);
  };

  clearWorkflow = () => {
    this.currentWorkflow = undefined;
  };

  setPageNum = (page: number) => {
    this.page = page;
  };

  setLimit = (limit: number) => {
    this.limit = limit;
    this.page = 1;
    this.refreshData();
  };

  setSearchTerm = (term: string) => {
    this.search = term;
    this.page = 1;
    this.refreshData();
  };

  setStatus = (status: string) => {
    this.status = status;
    this.page = 1;
    this.refreshData();
  };

  setNodesStatus = (nodesStatus: string) => {
    this.nodesStatus = nodesStatus;
    this.page = 1;
    this.refreshData();
  };

  setCategory = (category: string) => {
    this.category = category;
    this.page = 1;
    this.refreshData();
  };

  resetFilters = () => {
    this.search = "";
    this.status = "all";
    this.nodesStatus = "all";
    this.category = "all";
    this.page = 1;
    this.refreshData();
  };

  get filteredWorkflows() {
    let filtered = this.data;

    // Filter by status
    if (this.status !== "all") {
      filtered = filtered.filter(workflow => workflow.status === this.status);
    }

    // Filter by nodesStatus
    if (this.nodesStatus !== "all") {
      filtered = filtered.filter(
        workflow => workflow.nodesStatus === this.nodesStatus,
      );
    }

    // Filter by category (if we have category field)
    if (this.category !== "all") {
      // This would need to be implemented based on your data structure
      // filtered = filtered.filter(workflow => workflow.category === this.category);
    }

    return filtered;
  }

  pauseWorkflow = async (id: string) => {
    try {
      await pauseWorkflowService(id);
      runInAction(() => {
        this.data = this.data.map(workflow => {
          if (workflow.id === id) {
            workflow.status = WorkflowStatusEnum.Active;
          }
          return workflow;
        });
      });
    } catch (error: any) {
      throw new Error(error.message || "Error pausing workflow");
    }
  };

  activateWorkflow = async (id: string) => {
    try {
      await activateWorkflowService(id);
      runInAction(() => {
        this.data = this.data.map(workflow => {
          if (workflow.id === id) {
            workflow.status = WorkflowStatusEnum.Active;
          }
          return workflow;
        });
      });
    } catch (error: any) {
      throw new Error(error.message || "Error activating workflow");
    }
  };

  exportWorkflow = async (id: string): Promise<Blob> => {
    try {
      const blob = await exportWorkflow(id);
      return blob;
    } catch (error: any) {
      throw new Error(error.message || "Error exporting workflow");
    }
  };

  duplicateWorkflow = async (id: string) => {
    try {
      const response = await duplicateWorkflow(id);
      runInAction(() => {
        this.data.unshift(response.data as unknown as Workflow);
        this.currentPaginationInfo.totalItems += 1;
      });
    } catch (error: any) {
      throw new Error(error.message || "Error duplicating workflow");
    }
  };

  archiveWorkflow = async (id: string) => {
    try {
      await archiveWorkflowService(id);
      runInAction(() => {
        this.data = this.data.map(workflow => {
          if (workflow.id === id) {
            workflow.status = WorkflowStatusEnum.Archived;
          }
          return workflow;
        });
      });
    } catch (error: any) {
      throw new Error(error.message || "Error archiving workflow");
    }
  };

  restoreWorkflow = async (id: string) => {
    try {
      await restoreWorkflowService(id);
      runInAction(() => {
        this.data = this.data.map(workflow => {
          if (workflow.id === id) {
            workflow.status = WorkflowStatusEnum.Active;
          }
          return workflow;
        });
      });
    } catch (error: any) {
      throw new Error(error.message || "Error restoring workflow");
    }
  };

  // New methods for enhanced functionality

  // Statistics and Analytics
  fetchWorkflowStatistics = async (startDate?: string, endDate?: string) => {
    try {
      runInAction(() => {
        this.isLoadingStatistics = true;
      });

      const { data } = await getWorkflowStatistics({ startDate, endDate });

      runInAction(() => {
        this.workflowStatistics = data;
        this.isLoadingStatistics = false;
      });
    } catch (error: any) {
      runInAction(() => {
        this.isLoadingStatistics = false;
      });
      toast.error("Error fetching workflow statistics");
    }
  };

  fetchWorkflowPerformance = async (
    workflowId?: string,
    startDate?: string,
    endDate?: string,
  ) => {
    try {
      runInAction(() => {
        this.isLoadingPerformance = true;
      });

      const { data } = await getWorkflowPerformance({
        workflowId,
        startDate,
        endDate,
      });

      runInAction(() => {
        this.workflowPerformance = data;
        this.isLoadingPerformance = false;
      });
    } catch (error: any) {
      runInAction(() => {
        this.isLoadingPerformance = false;
      });
      toast.error("Error fetching workflow performance");
    }
  };

  fetchWorkflowAnalytics = async (startDate?: string, endDate?: string) => {
    try {
      runInAction(() => {
        this.isLoadingAnalytics = true;
      });

      const { data } = await getWorkflowAnalytics({ startDate, endDate });

      runInAction(() => {
        this.workflowAnalytics = data;
        this.isLoadingAnalytics = false;
      });
    } catch (error: any) {
      runInAction(() => {
        this.isLoadingAnalytics = false;
      });
      toast.error("Error fetching workflow analytics");
    }
  };

  // Version Control
  fetchWorkflowVersions = async (workflowId: string) => {
    try {
      runInAction(() => {
        this.isLoadingVersions = true;
      });

      const { data } = await getWorkflowVersions(workflowId);

      runInAction(() => {
        this.workflowVersions = data;
        this.isLoadingVersions = false;
      });
    } catch (error: any) {
      runInAction(() => {
        this.isLoadingVersions = false;
      });
      toast.error("Error fetching workflow versions");
    }
  };

  // Validation
  validateWorkflowNodes = async (workflowId: string, nodes: any[]) => {
    try {
      const { data } = await validateWorkflow(workflowId, nodes);
      return data;
    } catch (error: any) {
      toast.error("Error validating workflow nodes");
      throw error;
    }
  };

  // Templates
  fetchWorkflowTemplates = async () => {
    try {
      runInAction(() => {
        this.isLoadingTemplates = true;
      });

      const { data } = await getWorkflowTemplates();

      runInAction(() => {
        this.workflowTemplates = data;
        this.isLoadingTemplates = false;
      });
    } catch (error: any) {
      runInAction(() => {
        this.isLoadingTemplates = false;
      });
      toast.error("Error fetching workflow templates");
    }
  };

  createFromTemplate = async (
    templateId: string,
    name: string,
    description?: string,
  ) => {
    try {
      const { data } = await createWorkflowFromTemplate(templateId, {
        name,
        description,
      });

      runInAction(() => {
        this.data.unshift(data as any);
        this.currentPaginationInfo.totalItems += 1;
      });

      toast.success("Workflow created from template successfully");
      return data;
    } catch (error: any) {
      if (!error?.handledByInterceptor) {
        const msg = error?.response?.data?.message || "Error creating workflow from template";
        toast.error(msg);
      }
      throw error;
    }
  };

  // Testing
  createTestSuite = async (
    workflowId: string,
    name: string,
    description: string,
    scenarios: any[],
  ) => {
    try {
      const { data } = await createTestSuite(workflowId, {
        name,
        description,
        scenarios,
      });

      runInAction(() => {
        this.testSuites.push(data);
      });

      toast.success("Test suite created successfully");
      return data;
    } catch (error: any) {
      toast.error("Error creating test suite");
      throw error;
    }
  };

  runTestScenario = async (
    workflowId: string,
    scenario: any,
    variables?: Record<string, any>,
  ) => {
    try {
      const { data } = await runTestScenario(workflowId, {
        scenario,
        variables,
      });
      return data;
    } catch (error: any) {
      toast.error("Error running test scenario");
      throw error;
    }
  };

  fetchTestHistory = async (workflowId: string, limit: number = 50) => {
    try {
      const { data } = await getTestHistory(workflowId, limit);

      runInAction(() => {
        this.testHistory = data;
      });
    } catch (error: any) {
      toast.error("Error fetching test history");
    }
  };

  // Workflow Execution
  fetchWorkflowExecutions = async (
    params: {
      workflowId?: string;
      status?: string;
      page?: number;
      limit?: number;
    } = {},
  ) => {
    try {
      runInAction(() => {
        this.isLoadingExecutions = true;
      });

      const { data } = await getWorkflowExecutions(params);

      runInAction(() => {
        this.workflowExecutions = data.executions;
        this.isLoadingExecutions = false;
      });
    } catch (error: any) {
      runInAction(() => {
        this.isLoadingExecutions = false;
      });
      toast.error("Error fetching workflow executions");
    }
  };

  fetchWorkflowExecution = async (executionId: string) => {
    try {
      const { data } = await getWorkflowExecution(executionId);

      runInAction(() => {
        this.currentExecution = data;
      });
    } catch (error: any) {
      toast.error("Error fetching workflow execution");
    }
  };

  fetchExecutionLogs = async (executionId: string) => {
    try {
      const { data } = await getWorkflowExecutionLogs(executionId);

      runInAction(() => {
        this.executionLogs = data;
      });
    } catch (error: any) {
      toast.error("Error fetching execution logs");
    }
  };

  deleteExecution = async (executionId: string) => {
    try {
      await deleteWorkflowExecution(executionId);

      runInAction(() => {
        this.workflowExecutions = this.workflowExecutions.filter(
          exec => exec.id !== executionId,
        );
      });

      toast.success("Execution deleted successfully");
    } catch (error: any) {
      toast.error("Error deleting execution");
    }
  };

  // Variables Management
  fetchWorkflowVariables = async (workflowId: string) => {
    try {
      runInAction(() => {
        this.isLoadingVariables = true;
      });

      const { data } = await getWorkflowVariables(workflowId);

      runInAction(() => {
        this.workflowVariables[workflowId] = data;
        this.isLoadingVariables = false;
      });
    } catch (error: any) {
      runInAction(() => {
        this.isLoadingVariables = false;
      });
      toast.error("Error fetching workflow variables");
    }
  };

  updateVariables = async (
    workflowId: string,
    variables: Record<string, any>,
  ) => {
    try {
      await updateWorkflowVariables(workflowId, variables);

      runInAction(() => {
        this.workflowVariables[workflowId] = Object.entries(variables).map(
          ([name, value]) => ({
            name,
            value,
            type: typeof value,
          }),
        );
      });

      toast.success("Variables updated successfully");
    } catch (error: any) {
      toast.error("Error updating variables");
    }
  };

  addVariable = async (
    workflowId: string,
    variable: { name: string; value: any; type: string },
  ) => {
    try {
      await addWorkflowVariable(workflowId, variable);

      runInAction(() => {
        if (!this.workflowVariables[workflowId]) {
          this.workflowVariables[workflowId] = [];
        }
        this.workflowVariables[workflowId].push(variable);
      });

      toast.success("Variable added successfully");
    } catch (error: any) {
      toast.error("Error adding variable");
    }
  };

  removeVariable = async (workflowId: string, variableName: string) => {
    try {
      await deleteWorkflowVariable(workflowId, variableName);

      runInAction(() => {
        if (this.workflowVariables[workflowId]) {
          this.workflowVariables[workflowId] = this.workflowVariables[
            workflowId
          ].filter(v => v.name !== variableName);
        }
      });

      toast.success("Variable removed successfully");
    } catch (error: any) {
      toast.error("Error removing variable");
    }
  };

  // Keywords Management
  fetchWorkflowKeywords = async (workflowId: string) => {
    try {
      runInAction(() => {
        this.isLoadingKeywords = true;
      });

      const { data } = await getWorkflowKeywords(workflowId);

      runInAction(() => {
        this.workflowKeywords[workflowId] = data;
        this.isLoadingKeywords = false;
      });
    } catch (error: any) {
      runInAction(() => {
        this.isLoadingKeywords = false;
      });
      toast.error("Error fetching workflow keywords");
    }
  };

  updateKeywords = async (workflowId: string, keywords: string[]) => {
    try {
      await updateWorkflowKeywords(workflowId, keywords);

      runInAction(() => {
        this.workflowKeywords[workflowId] = keywords.map(keyword => ({
          keyword,
          action: "trigger",
        }));
      });

      toast.success("Keywords updated successfully");
    } catch (error: any) {
      toast.error("Error updating keywords");
    }
  };

  addKeyword = async (
    workflowId: string,
    keyword: { keyword: string; action: string },
  ) => {
    try {
      await addWorkflowKeyword(workflowId, keyword);

      runInAction(() => {
        if (!this.workflowKeywords[workflowId]) {
          this.workflowKeywords[workflowId] = [];
        }
        this.workflowKeywords[workflowId].push(keyword);
      });

      toast.success("Keyword added successfully");
    } catch (error: any) {
      toast.error("Error adding keyword");
    }
  };

  removeKeyword = async (workflowId: string, keyword: string) => {
    try {
      await deleteWorkflowKeyword(workflowId, keyword);

      runInAction(() => {
        if (this.workflowKeywords[workflowId]) {
          this.workflowKeywords[workflowId] = this.workflowKeywords[
            workflowId
          ].filter(k => k.keyword !== keyword);
        }
      });

      toast.success("Keyword removed successfully");
    } catch (error: any) {
      toast.error("Error removing keyword");
    }
  };

  // Simulation
  simulateWorkflowExecution = async (
    workflowId: string,
    input: any,
    variables?: Record<string, any>,
  ) => {
    try {
      const { data } = await simulateWorkflow(workflowId, { input, variables });
      return data;
    } catch (error: any) {
      toast.error("Error simulating workflow");
      throw error;
    }
  };

  fetchSimulationHistory = async (workflowId: string) => {
    try {
      const { data } = await getSimulationHistory(workflowId);

      runInAction(() => {
        this.simulationHistory = data;
      });
    } catch (error: any) {
      toast.error("Error fetching simulation history");
    }
  };

  // Utility methods
  getWorkflowById = (id: string) => {
    return this.data.find(workflow => workflow.id === id);
  };

  getWorkflowVariablesById = (workflowId: string) => {
    return this.workflowVariables[workflowId] || [];
  };

  getWorkflowKeywordsById = (workflowId: string) => {
    return this.workflowKeywords[workflowId] || [];
  };

  updateWorkflow = async (id: string, updateData: Partial<Workflow>) => {
    try {
      // First update on the server
      const response = await apiUpdateWorkflow(id, updateData);

      // Then update local store
      runInAction(() => {
        this.data = this.data.map(workflow => {
          if (workflow.id === id) {
            return { ...workflow, ...updateData };
          }
          return workflow;
        });

        // Also update currentWorkflow if it's the one being edited
        if (this.currentWorkflow?.id === id) {
          this.currentWorkflow = { ...this.currentWorkflow, ...updateData };
        }
      });

      toast.success("Workflow updated successfully");
      return response.data;
    } catch (error: any) {
      console.error("Error updating workflow:", error);
      toast.error(error.message || "Error updating workflow");
      throw error;
    }
  };

  clearWorkflowData = () => {
    this.currentWorkflow = undefined;
    this.workflowVersions = [];
    this.testSuites = [];
    this.testHistory = [];
    this.workflowExecutions = [];
    this.currentExecution = null;
    this.executionLogs = [];
    this.simulationHistory = [];
  };

  // ==========================================================================
  // Workflow Name Cache (for campaign display)
  // ==========================================================================

  /**
   * Get workflow name from cache or fetch from API
   * Returns cached name if valid, otherwise fetches and caches
   *
   * @param workflowId - The workflow ID to get the name for
   * @returns Promise<string> - The workflow name or "Unknown Workflow" if not found
   */
  getWorkflowName = async (workflowId: string): Promise<string> => {
    if (!workflowId) {
      return "Unknown Workflow";
    }

    // Check cache first
    const cached = this.workflowNameCache.get(workflowId);
    if (cached) {
      const age = Date.now() - cached.fetchedAt.getTime();
      if (age < this.WORKFLOW_CACHE_TTL) {
        return cached.name;
      }
      // Cache expired, remove it
      this.workflowNameCache.delete(workflowId);
    }

    // Check if workflow is in the existing data array
    const existingWorkflow = this.data.find(w => w.id === workflowId);
    if (existingWorkflow) {
      this.setWorkflowNameCache(workflowId, existingWorkflow.name);
      return existingWorkflow.name;
    }

    // Fetch from API
    try {
      const { data } = await getWorkflow(workflowId);
      const name = (data as any).name || "Unknown Workflow";
      this.setWorkflowNameCache(workflowId, name);
      return name;
    } catch (error) {
      console.warn(
        `Failed to fetch workflow name for ID ${workflowId}:`,
        error,
      );
      return "Unknown Workflow";
    }
  };

  /**
   * Get cached workflow name synchronously (returns null if not cached)
   * Use this for initial render, then call getWorkflowName() in useEffect
   */
  getCachedWorkflowName = (workflowId: string): string | null => {
    if (!workflowId) return null;

    const cached = this.workflowNameCache.get(workflowId);
    if (cached) {
      const age = Date.now() - cached.fetchedAt.getTime();
      if (age < this.WORKFLOW_CACHE_TTL) {
        return cached.name;
      }
    }
    return null;
  };

  /**
   * Set workflow name in cache
   */
  setWorkflowNameCache = (workflowId: string, name: string): void => {
    runInAction(() => {
      this.workflowNameCache.set(workflowId, {
        name,
        fetchedAt: new Date(),
      });
    });
  };

  /**
   * Clear all cached workflow names
   */
  clearWorkflowNameCache = (): void => {
    runInAction(() => {
      this.workflowNameCache.clear();
    });
  };
}
